/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FolderIconFilled, LogoMediumIcon, Photo, PlusLargeIcon } from 'shared/components/Icons';
import { addQueryParam, getQueryParam } from 'utils/locationHelper';
import { getCategories, getSharedProjects, getDashboardStats } from '../../actions/home';
import { getFromStore } from 'utils/storeHelpers';
import { openOnBoardModal, scrollToElement } from 'shared/components/layout/OnBoarding';
import { isMobileSize } from 'shared/utils/appHelpers';
import { getProjects } from '../../actions/projects';
import { removeProductModal } from 'shared/utils/productHelpers';
import { useAlert } from 'react-alert';
import Button from 'shared/components/Button';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import ProjectModal from './components/ProjectModal';
import AddProductModal from 'shared/components/likiee/AddProductModal';
import Projects from './components/Projects';
import Dropdown from 'shared/components/Dropdown';
import Categories from './components/Categories';
import StepAddLikiee from 'shared/components/layout/OnBoarding/components/StepAddLikiee';
import StepAddProject from 'shared/components/layout/OnBoarding/components/StepAddProject';
import StepAddLikieeExt from 'shared/components/layout/OnBoarding/components/StepAddLikieeExt';
import StepCategory from 'shared/components/layout/OnBoarding/components/StepCategory';
import RecomendedItems from './components/RecomendedItems';
import AddProductByImageModal from 'shared/components/likiee/AddProductByImageModal/index';
import SaveToProjectModal from 'shared/components/likiee/SaveToProjectModal';
import { useBackgroundSync } from 'hooks/useBackgroundSync';
import { CACHE_TTL } from 'utils/cacheHelpers';

const Home = ({ history }) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const projects = useSelector(state => state.projects);
  const homeData = useSelector(state => state.home);
  const [dropMenu, setDropMenu] = useState(false);
  const [addProductState, setAddProductState] = useState(false);
  const [addProductPhotoState, setAddProductPhotoState] = useState(false);
  const [disableOutSideClick, setDisableOutSideClick] = useState();
  const [productForProject, setProductForProject] = useState(false);
  const projectModalId = getQueryParam('pj');
  const isMobile = isMobileSize(window.innerWidth);

  const handleProjectModalOpen = () => {
    history.push(addQueryParam('pj', 'new'));
  };

  const showLikieeCategoryOnboardingWondow = () => {
    if (isMobile) document.querySelector('html').scrollTop = 0;
    openOnBoardModal({
      type: 'onboarding_likiee_category',
      content: [
        {
          el: '.onboarding-sel-category',
          elMobile: '.category-block',
          filterEls: ['header', 'footer', '.onboarding-sel-button-area', '.project-items', '.dropdown'],
          position: 'bottom',
          component: <StepCategory />,
        },
      ],
    });
  };

  const showLikieeOnboardingWindow = showCategories => {
    if (isMobile) scrollToElement('.onboarding-sel-button-area');
    setDropMenu(true);
    setDisableOutSideClick(true);
    setTimeout(() => {
      const blurEls = ['header', 'footer', '.dash-content h1', '.project-items', '.category-block'];
      openOnBoardModal({
        onCloseHandler: () => {
          setDropMenu(false);
          setDisableOutSideClick(false);
          if (showCategories) showLikieeCategoryOnboardingWondow();
        },
        type: 'onboarding_likiee_project',
        content: [
          {
            el: '.onboarding-sel-add-likiee',
            elMobile: '.onboarding-sel-button-area .drop-content',
            filterEls: [...blurEls, '.onboarding-sel-add-project'],
            position: isMobile ? 'bottom' : 'left',
            className: 'onboard-add-likie',
            component: <StepAddLikiee />,
          },
          {
            el: '',
            filterEls: [...blurEls, '.dash-content'],
            position: isMobile ? 'bottom' : 'left',
            className: 'onboard-add-likie-ext',
            component: <StepAddLikieeExt />,
          },
          {
            el: '.onboarding-sel-add-project',
            filterEls: [...blurEls, '.onboarding-sel-add-likiee'],
            position: isMobile ? 'bottom' : 'left',
            className: 'onboard-add-project',
            component: <StepAddProject />,
          },
        ],
      });
    }, 100);
  };

  const onSuccessCategories = hasData => {
    const showLikieeOnBoarding = !getFromStore('onboarding_likiee_project');
    const showCategoryOnBoarding = !getFromStore('onboarding_likiee_category');

    if (showLikieeOnBoarding) {
      setTimeout(showLikieeOnboardingWindow, 100, showCategoryOnBoarding && hasData);
    }

    if (!showLikieeOnBoarding && showCategoryOnBoarding && hasData) {
      setTimeout(showLikieeCategoryOnboardingWondow, 100);
    }
  };

  const onProductRemove = product => {
    removeProductModal(product, alert, t, getDashboardStats);
  };

  const onClickProductProject = product => {
    setProductForProject(product);
  };

  const getData = async () => {
    try {
      // These will use cached data if valid, or fetch fresh data
      getDashboardStats();
      getProjects();
      getSharedProjects();
      getCategories(onSuccessCategories);
    } catch (err) {
      alert.error(String(err));
    }
  };

  // Background refresh when cache is stale (stale-while-revalidate pattern)
  // This ensures users see cached data instantly, but get fresh data in background
  useBackgroundSync(getDashboardStats, 'dashboard_stats', CACHE_TTL.DASHBOARD_STATS);
  useBackgroundSync(getProjects, 'projects', CACHE_TTL.PROJECTS);
  useBackgroundSync(getSharedProjects, 'shared_projects', CACHE_TTL.SHARED_PROJECTS);
  useBackgroundSync(() => getCategories(onSuccessCategories), 'categories', CACHE_TTL.CATEGORIES);

  useEffect(() => {
    getData();
  }, []);

  return (
    <div>
      {!!homeData?.categories?.length && <Categories title={t('title_categories')} data={homeData.categories} />}
      <div className='d-flex align-items-center justify-content-between mb-8 mb-md-12 onboarding-sel-button-area'>
        <h1 className='mb-0'>{t('title_projects')}</h1>
        <Dropdown
          className='ml-8 ml-md-16'
          contentClassName='no-separator mt-6 py-1 dash-add-drop-menu justify-content-between'
          withPortal={false}
          outStateOpen={dropMenu}
          setOutStateOpen={setDropMenu}
          disableOutSideClick={disableOutSideClick}
          toggleContent={isOpened => (
            <Button
              className={`btn-sm d-flex no-transition align-items-center px-8 py-2 py-md-4 ${
                isOpened ? 'btn-dark' : 'btn-outline-dark'
              }`}
            >
              <PlusLargeIcon className='mr-5' />
              {t('btn_add')}
            </Button>
          )}
        >
          <div className='px-8'>
            <Dropdown.Item
              className='d-flex text-nowrap align-items-center px-0 border-bottom onboarding-sel-add-likiee'
              onClick={() => setAddProductState(true)}
            >
              <LogoMediumIcon className='mr-5 svg-gray min-icon' width='24' height='24' />
              <span className='text-ellipsis text-md'>{t('btn_add_likiee')}</span>
            </Dropdown.Item>
            <Dropdown.Item
              className='d-flex text-nowrap px-0 align-items-center border-bottom onboarding-sel-add-project'
              onClick={() => setAddProductPhotoState(true)}
            >
              <Photo className='mr-5 svg-gray min-icon' width='24' height='24' />
              <span className='text-ellipsis text-md'>{t('btn_add_likiee_photo')}</span>
            </Dropdown.Item>
            <Dropdown.Item
              className='d-flex text-nowrap align-items-center px-0 onboarding-sel-add-project'
              onClick={handleProjectModalOpen}
            >
              <FolderIconFilled className='mr-5 svg-gray min-icon' width='24' height='24' />
              <span className='text-ellipsis text-md'>{t('btn_add_new_project')}</span>
            </Dropdown.Item>
          </div>
        </Dropdown>
      </div>
      {projects ? (
        <Projects data={Object.values(projects)} onCreateNewProject={handleProjectModalOpen} />
      ) : (
        <SkeletonLoader type='projects' count={6} />
      )}
      {!!homeData?.mostPriceDropped?.length && (
        <RecomendedItems
          title={t('title_recent_price_drops')}
          data={homeData?.mostPriceDropped}
          onProductRemove={onProductRemove}
          onClickProductProject={onClickProductProject}
        />
      )}
      {!!homeData?.sharedProjects?.length && (
        <Projects isShared={true} title={t('title_shared_projects')} data={homeData?.sharedProjects} />
      )}
      {!!homeData?.recentLikiees?.length && (
        <RecomendedItems
          title={t('title_recent_likiees')}
          data={homeData?.recentLikiees}
          onProductRemove={onProductRemove}
          onClickProductProject={onClickProductProject}
        />
      )}
      {!!homeData?.followersProjects?.length && (
        <Projects isShared={true} title={t('title_followers_projects')} data={homeData?.followersProjects} />
      )}
      {projectModalId && <ProjectModal projectModalId={projectModalId} />}
      {addProductState && <AddProductModal onClose={() => setAddProductState(false)} />}
      {addProductPhotoState && <AddProductByImageModal onClose={() => setAddProductPhotoState(false)} />}
      {productForProject && (
        <SaveToProjectModal
          productID={productForProject.id}
          initialProjectIDs={productForProject.projects?.items.map(i => i.id) || []}
          onClose={() => {
            setProductForProject(false);
          }}
          onSuccess={() => {
            getDashboardStats();
            getProjects();
          }}
        />
      )}
    </div>
  );
};

export default Home;
